import { JoinAdvancedFilterModel, NumberAdvancedFilterModel, ScalarAdvancedFilterModelType } from 'ag-grid-enterprise';
import { of } from 'rxjs';

import { InputChoices } from '@camelot/form-model';
import { CriticityStatus, criticityLabel } from '@camelot/ui';
import { extractEnum, isNonNullable } from '@camelot/utils';

import { GridCriticityCellComponent } from '../../../components/templates/criticity/criticity.component';
import { BaseCol, IInputOptions } from '../base-col';
import { EnumEditorComponent } from './enum-editor.component';
import { EnumFilterComponent } from './enum-filter.component';

type FilterTyping = JoinAdvancedFilterModel | NumberAdvancedFilterModel;

export class EnumCol extends BaseCol<FilterTyping> {
  get values() {
    return this._extractFiltersByFilterType(this.filterInstance, ['contains', 'equals'])
      .filter(cond => cond.filterType === 'number')
      .map((cond: any) => cond.filter?.toString())
      .filter(isNonNullable);
  }
  public override getInputForm(options?: IInputOptions) {
    const enumValues = this.getEnumInfo().values.map(v => ({ ...v, data: v.id }));

    return new InputChoices({
      key: this.key,
      label: options?.fromView ? '' : this.inputLabel,
      multiple: options?.fromView ? true : false,
      onlyTemplate: options?.fromView,
      options: of(enumValues),
      value: this.values,
      withSearch: true,
    });
  }
  public override getFiltersModel(data: any): FilterTyping | null {
    const value = data[this.key];
    if (!value) {
      return null;
    }

    const type = <ScalarAdvancedFilterModelType>(this.data.col.isCollection ? 'contains' : 'equals');

    if (!Array.isArray(value)) {
      return {
        filterType: 'number',
        colId: this.key,
        type: type,
        filter: Number(value),
      };
    }
    const filters = value.reduce<NumberAdvancedFilterModel[]>((acc, val) => {
      if (val) {
        acc.push({
          filterType: 'number',
          colId: this.key,
          type: type,
          filter: Number(val),
        });
      }
      return acc;
    }, []);

    if (filters.length === 0) {
      return null;
    }
    if (filters.length === 1) {
      return filters[0];
    }
    return {
      filterType: 'join',
      type: 'OR',
      conditions: filters,
    };
  }

  public override getColDef() {
    const cellRenderer = this.getEnumInfo().cellRenderer;
    return {
      ...super.getColDef(),
      ...{
        cellEditor: EnumEditorComponent,
        cellRenderer: cellRenderer,
        filter: EnumFilterComponent,
        type: 'string',
      },
    };
  }

  public getEnumInfo(): {
    cellRenderer: any | null;
    enum: any | null;
    values: {
      id: string;
      name: string;
    }[];
  } {
    if (this.data.options.enumInfo) {
      return this.data.options.enumInfo;
    }
    const metaEnumInfo = this.data.col.enumInfo;
    if (metaEnumInfo) {
      return {
        cellRenderer: metaEnumInfo.cellRenderer,
        enum: metaEnumInfo.enum,
        values: extractEnum(metaEnumInfo.enum, true).map(value => ({
          id: value.value.toString(),
          name: metaEnumInfo.labelFn(value.value),
        })),
      };
    }
    switch (this.data.col.enumName) {
      case 'Criticity':
        return {
          cellRenderer: GridCriticityCellComponent,
          enum: CriticityStatus,
          values: extractEnum(CriticityStatus, true).map(value => ({
            id: value.value.toString(),
            name: criticityLabel(value.value),
          })),
        };
      default:
        return { cellRenderer: null, enum: null, values: [] };
    }
  }
}
